/**
 * bomApprovedUpdate.js — which version of a library BOM, if any, a linked workflow should be
 * offered to move to (S122/S135/S148, HU-07).
 *
 * Corrección PO 2026-09-10: only an **APPROVED** version is worth interrupting anybody for. A DRAFT
 * or a PROPOSAL is work someone is still doing on the BOM, and a workflow in production must not be
 * dragged behind it. Sun answers `updateAvailable`/`latestBomId`/`latestVersionNumber` in N15
 * WITHOUT looking at the index (contrato-sun.md §7, D19), so its "latest" may well be a draft: the
 * rule is therefore applied here, over the versions N2 (`getBomLibraryDetail`) returns.
 *
 * Pure on purpose — the decision of what counts as an update is a rule, not a rendering detail, and
 * it is the same one the banner, the dismissal (N18) and the update (N20) must all agree on.
 */

/** The only index that makes a version something to offer (corrección PO 2026-09-10). */
const OFFERABLE_INDEX = 'APPROVED'

/**
 * @param {object} link the `WorkflowBomLink` of the workflow (contrato-sun.md §2)
 * @param {Array<object>} versions the `versions` of the BOM's `BomLibraryDetail` (N2)
 * @returns {object} `{bomId, versionNumber, indexValue}` of the version to offer, or `{}` if there
 * is none: nothing newer, nothing approved, or the highest approved one already dismissed (RN-07.7).
 */
export const resolveApprovedUpdate = (link = {}, versions = []) => {
  if (!link?.linked) return {}
  const linkedVersion = Number(link.versionNumber || 0)
  const dismissedVersion = Number(link.dismissedVersionNumber || 0)
  const floor = Math.max(linkedVersion, dismissedVersion)

  const candidate = (versions || [])
    .filter((version) => version?.indexValue === OFFERABLE_INDEX && Number(version.versionNumber) > floor)
    // The highest approved one: it is what the banner announces, what N18 records as dismissed and
    // what N20 is asked to move to, so the three cannot drift apart.
    .reduce((best, version) => (Number(version.versionNumber) > Number(best?.versionNumber || 0) ? version : best), null)

  if (!candidate) return {}
  return { bomId: candidate.bomId, versionNumber: candidate.versionNumber, indexValue: candidate.indexValue }
}
